// The grounded control a row renders below itself — a distribution, value pills, or segments.
// Present or absent: an absent (or empty / ill-fitting) control renders nothing. Owns the pill
// single/multi/operator-widen semantics and reflects the predicate's current value; edits flow back
// through a single atomic `onCommit`.

import { booleanFromPill } from '@server/audience-boolean-pill';

import { cn } from '@/_shared/lib/utils';

import { asBooleanPill, asEnumMulti, asEnumSingle } from '../lib/operators';
import type { ConditionValue, OperatorValue, RowControl, ValueShape } from '../types';
import { ApproxDistribution } from './ApproxDistribution';
import { EnumValuePills } from './EnumValuePills';
import { fillRateLine, valueCoverageLine } from './lib/coverage-text';
import { domainForShape, isWindow } from './lib/distribution-domains';
import { insightState } from './lib/insight-fit';
import { NumericDistribution } from './NumericDistribution';
import { SegmentPills } from './SegmentPills';
import type { PillValue, PredicatePatch, RowSegment } from './types';
import { pillRaws, segmentActive } from './types';

/** An atomic predicate edit merged into the predicate on pick. */
export type InsightCommit = PredicatePatch;

/**
 * Whether the predicate carries a value at all — the panel's read of the same "committed" the threshold
 * slider derives from its parsed handles. A range counts only when BOTH ends are set. It gates the
 * fill-rate DISCLAIMER, which explains a percentage: with no threshold there is no percentage on screen,
 * so the sentence would otherwise sit under a control reading "No threshold set".
 */
function hasCommittedValue(value: ConditionValue): boolean {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.length > 0 && value.every((v) => v !== null && v !== undefined);
  return true;
}

/**
 * The grounding footnote. ONE renderer, so it reads identically with or without a widget, and ONE line:
 * these are two clauses of a single statement about the same column, and stacking them gave a two-line
 * block more visual weight than the control it was annotating.
 */
function Footnotes({ lines, className }: { lines: string[]; className?: string }) {
  if (lines.length === 0) return null;
  return <p className={cn('text-[0.6875rem] text-muted-foreground italic', className)}>{lines.join(' · ')}</p>;
}

const NEGATION_OPERATORS = new Set(['nin', 'neq']);

/** The observed value(s) already chosen, as a set of PILL SPELLINGS. `''` counts as chosen. */
function selectedValues(value: ConditionValue, shape: ValueShape): string[] {
  // A boolean predicate holds a JSON boolean, which `asEnumSingle` reads as `undefined` — so without
  // this the pill for the value the rule actually uses would render unselected, and re-clicking it
  // would re-select rather than clear it.
  if (shape === 'boolean') {
    const pill = asBooleanPill(value);
    return pill !== undefined ? [pill] : [];
  }
  if (Array.isArray(value)) return asEnumMulti(value);
  const single = asEnumSingle(value);
  return single !== undefined ? [single] : [];
}

/**
 * The operator FOLLOWS the selection rather than constraining it: pills always toggle, and the count that
 * comes out decides the word. One value reads `is`, several read `is one of`, and the negated pair mirrors
 * it — so a reader never has to change the operator before picking a second value, and a rule can never
 * say `is` over a list. Clearing the last value keeps the scalar word with no value, which is what the
 * summary already renders as unset.
 */
const operatorForCount = (count: number, negated: boolean): OperatorValue =>
  count > 1 ? (negated ? 'nin' : 'in') : negated ? 'neq' : 'eq';

export function InsightPanel({
  control,
  shape,
  operator,
  value,
  onCommit,
  fieldPath,
  nonNullFraction,
  disabled = false,
}: {
  control?: RowControl; // absent / empty / ill-fitting → renders nothing
  shape: ValueShape;
  operator: string;
  value: ConditionValue;
  onCommit: (next: InsightCommit) => void;
  fieldPath?: string;
  /** The column's fill rate, from the Row. Absent ⇒ NOT MEASURED — the line goes quiet, never prints 0%. */
  nonNullFraction?: number;
  disabled?: boolean;
}) {
  const ready = control ?? null;
  const domain = domainForShape(shape, undefined, operator, isWindow(value) ? value.unit : undefined);
  const pills = ready?.kind === 'pills' ? ready.values : [];
  const isChart = ready?.kind === 'distribution' || ready?.kind === 'quantiles';

  // Whether there is a control here at all is `insightState`'s call, not this component's — the clause
  // gates the toggle that opens the panel on the same answer, and the two must not be able to disagree.
  const state = insightState(control, shape, operator, value, nonNullFraction);

  // A boolean column offers eq/neq only (`ATTRIBUTE_OPERATORS.boolean`), so the operator must NOT follow
  // the selection the way it does for a string column — there is no `in`/`nin` to widen to, and its two
  // values are distinct fold keys that can never merge into one pill.
  const isBoolean = shape === 'boolean';
  const selected = selectedValues(value, shape);

  // Two clauses, most fundamental first: how much of the table has a value at all, then how much of
  // THAT the offered values account for. Computed BEFORE the early returns, so the grounding-only panel
  // states the same fill rate a control-bearing one does — the sentence must not depend on whether a
  // widget happened to render. The control-specific half stays gated on one: a pill list nobody can see
  // has no coverage worth quoting.
  // `quantiles` AND `distribution` both quote a share of the NON-NULL rows: the histogram's `total` is
  // the histogram's own `nonNull`, not the table's row count, so with no fill rate its "(x% of N)" reads
  // as a share of everyone exactly as the slider's would. Pills and segments divide by the row count and
  // have nothing to disclaim.
  const quotesNonNullShare =
    state === 'control' && (ready?.kind === 'quantiles' || ready?.kind === 'distribution') && hasCommittedValue(value);
  const footnotes = [
    fillRateLine(nonNullFraction, quotesNonNullShare),
    // `valueCount` from the row, NOT a count of rendered chips: the share and its count have to come from
    // one measurement, or the sentence pairs a percentage with a domain it never measured.
    state === 'control' && ready?.kind === 'pills'
      ? valueCoverageLine(ready.valueCoverage, ready.valueCount ?? 0)
      : undefined,
  ].filter((line): line is string => line !== undefined);

  const commit = (nextOp: OperatorValue, next: ConditionValue) => {
    const patch: PredicatePatch = nextOp === operator ? { value: next } : { value: next, operator: nextOp };
    onCommit(patch);
  };

  const onPick = (pill: PillValue) => {
    // Every pick is a toggle, including a folded pill, which carries all of its spellings in one go.
    const raws = pillRaws(pill);
    const r = raws[0]!;
    if (isBoolean) {
      // The operator is carried through untouched — `ATTRIBUTE_OPERATORS.boolean` is eq/neq only, so
      // there is no list operator for the selection count to widen into. (The prop is typed `string`
      // for every caller; the same narrowing is done below for `activeSegment`.)
      const keep = operator as OperatorValue;
      // Clearing comes first: re-clicking the selected pill unsets the value, same as any single-select.
      if (asBooleanPill(value) === r) {
        commit(keep, null);
        return;
      }
      // Then decode back to the JSON boolean the compiler needs — committing the `'true'` spelling
      // itself would compile to `= 'true'` against a boolean column. An UNRECOGNISED spelling is
      // declined outright: a decoder that answered `false` for it would write the inverse of what was
      // clicked, indistinguishably from a real `false`.
      const decoded = booleanFromPill(r);
      if (decoded === undefined) return;
      commit(keep, decoded);
      return;
    }
    const allIn = raws.every((v) => selected.includes(v));
    const next = allIn
      ? selected.filter((v) => !raws.includes(v))
      : [...selected, ...raws.filter((v) => !selected.includes(v))];
    // A list of one is written as the scalar the scalar operator expects, never a one-member array.
    commit(operatorForCount(next.length, NEGATION_OPERATORS.has(operator)), next.length > 1 ? next : (next[0] ?? null));
  };

  const onSegment = (seg: RowSegment) => onCommit(seg.patch);
  const activeSegment =
    ready?.kind === 'segments'
      ? ready.segments.findIndex((s) => segmentActive({ operator: operator as OperatorValue, value }, s.patch))
      : -1;

  // A chart measured for the PREVIOUS operator is withheld until a fresh one arrives. Saying so beats
  // the panel silently emptying: the reader changed one operator and the chart, the handle and the count
  // all disappeared with no indication that anything was wrong or that anything would bring them back.
  if (state === 'stale') {
    return (
      <div className="-mt-1.5 flex max-w-150 flex-col gap-1">
        <p className="text-xs text-muted-foreground italic">
          This chart was measured for the previous operator, so it is not shown. It returns once the rule is re-counted.
        </p>
        {/* The chart is withheld; the COLUMN is still measured, and its fill rate is as true here as
            anywhere else. Withholding it too would make the sentence depend on the widget again. */}
        <Footnotes lines={footnotes} />
      </div>
    );
  }
  // A measured column with no widget that fits. Say what is true about it rather than rendering nothing —
  // and use the stale line's bare frame, not the control's box: a border around one sentence reads as an
  // empty widget that failed to load.
  if (state === 'grounding') return <Footnotes lines={footnotes} className="-mt-1.5 max-w-150" />;
  if (!(ready && state === 'control')) return null;

  return (
    <fieldset disabled={disabled} className="-mt-1.5 max-w-150 rounded-lg border border-grey-100 bg-background p-3">
      {/* The scroll box wraps the CONTROL only. With the footnote inside it, a list long enough to scroll
          — the very case where "is this a slice?" is the live question — pushed the answer out of view. */}
      <div className={cn(!isChart && 'scrollbar-none max-h-64 overflow-x-hidden overflow-y-auto')}>
        {ready.kind === 'distribution' ? (
          <NumericDistribution
            key={`${shape}-${operator}`}
            bins={ready.bins}
            stats={ready.stats}
            total={ready.total}
            step={ready.step}
            scale={ready.scale}
            estimated={ready.estimated ?? false}
            domain={
              ready.unit
                ? domainForShape(shape, ready.unit, operator, isWindow(value) ? value.unit : undefined)
                : domain
            }
            operator={operator}
            value={value}
            onPatch={(bare) => onCommit({ value: bare })}
            ariaLabel={fieldPath ? `${fieldPath} threshold` : 'Threshold'}
            disabled={disabled}
          />
        ) : ready.kind === 'quantiles' ? (
          <ApproxDistribution
            key={`${shape}-${operator}`}
            quantiles={ready.quantiles}
            min={ready.min}
            max={ready.max}
            nonNullFraction={nonNullFraction ?? ready.nonNullFraction}
            step={ready.step}
            total={ready.total}
            totalAccuracy={ready.totalAccuracy}
            domain={domain}
            operator={operator}
            value={value}
            onPatch={(bare) => onCommit({ value: bare })}
            ariaLabel={fieldPath ? `${fieldPath} threshold` : 'Threshold'}
            disabled={disabled}
          />
        ) : ready.kind === 'segments' ? (
          <SegmentPills
            segments={ready.segments}
            total={ready.total}
            totalAccuracy={ready.totalAccuracy}
            estimated={ready.estimated ?? false}
            activeIndex={activeSegment}
            onPick={onSegment}
            ariaLabel={fieldPath ? `${fieldPath} range` : 'Range'}
          />
        ) : (
          <EnumValuePills
            values={pills}
            total={ready.total}
            totalAccuracy={ready.totalAccuracy}
            selected={selected}
            onPick={onPick}
            ariaLabel={fieldPath ? `${fieldPath} values` : 'Values'}
            negate={NEGATION_OPERATORS.has(operator)}
          />
        )}
      </div>
      {/* Shared grounding footnotes for whichever control was drawn above — OUTSIDE the scroll box, so a
          long pill list cannot clip the sentence that says whether it is the whole list. */}
      <Footnotes lines={footnotes} className="mt-2" />
    </fieldset>
  );
}
